/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { Text, FontIcon } from '@bingads-webui-react/fabric-control';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Link } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { getAdsStepStyles } from './styles';
import { CropPreview } from './cropped-preview';
import { getTargetOptions } from './image-targets';

const ITEMS_PER_PAGE = 10;

export const AdPreview = (props) => {
  const {
    i18n,
    ads,
    onUpdateCurrentImageIndex,
    disablePagination,
    customStyleFunction,
    useCustomStyleFunction,
  } = props;

  const displayAds = (ads && ads.data) || [];
  const defaultClasses = useDefaultStyles(getAdsStepStyles);
  const customClasses = useDefaultStyles(customStyleFunction);
  const classes = useCustomStyleFunction ? customClasses : defaultClasses;
  const { currentPageIndex } = ads;
  const [currentImageIdx, setCurrentImageIdx] = useState(0);
  const targetOptions = useMemo(() => getTargetOptions({ i18n }), [i18n]);

  const getPageAdjustedIndex = (idx, pageNumber = null) => {
    const page = pageNumber || currentPageIndex;

    const val = (((page - 1) * ITEMS_PER_PAGE) + idx);

    return val;
  };

  const goToPrevImage = () => {
    setCurrentImageIdx(currentImageIdx - 1);
  };

  const goToNextImage = () => {
    setCurrentImageIdx(currentImageIdx + 1);
  };

  const goToImageIndex = (idx) => {
    setCurrentImageIdx(idx);
  };

  const displayAdsArray = _.isArray(displayAds) ? displayAds : [];
  const displayAdsInPage = displayAdsArray.slice(getPageAdjustedIndex(0), getPageAdjustedIndex(9) + 1);
  const currentAd = displayAds[getPageAdjustedIndex(currentImageIdx)] || displayAds[0];
  const currentImage = (currentAd && currentAd.data && currentAd.data.image) || undefined;
  const resizeImageHeight = useMemo(() => {
    if (currentImage && currentImage.cropHeight && currentImage.cropWidth) {
      const aspectRatio = currentImage.cropWidth / currentImage.cropHeight;
      const maxWidth = 458;
      return Math.min(maxWidth / aspectRatio, 280);
    }
    return 0;
  }, [currentImage]);

  const currentImageTarget = useMemo(() => {
    if (!currentImage) {
      return null;
    }
    const target = targetOptions.find(option => option.key === `Image${currentImage.targetWidth}x${currentImage.targetHeight}`);

    return target || null;
  }, [currentImage, targetOptions]);

  useEffect(() => {
    setCurrentImageIdx(0);
  }, [currentPageIndex]);

  useEffect(() => {
    if (currentImageIdx > displayAdsInPage.length - 1) {
      setCurrentImageIdx(0);
    }
    onUpdateCurrentImageIndex(currentImageIdx);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentImageIdx, displayAdsInPage.length]);

  return (
    <div className={classes.adPreviewRoot}>
      <div className={classes.adPreviewPageHeader}>
        <Text className={classes.adPreviewPageTitle}> {`${i18n.getString(_TL_('Preview'))}: ${i18n.getString(_TL_('Ad'))} ${currentImageIdx + 1}`} </Text>
      </div>
      <div className={classes.adPreviewPanel}>
        <div className={classes.adPreviewSizeHeader}>
          {currentImageTarget &&
            <div>
              <Text className={classes.adPreviewSizeTitle}> {currentImageTarget.sizeText}
              </Text>
              <Text className={classes.adPreviewTargetTitle}> {currentImageTarget.targetText}
              </Text>
            </div>
          }
        </div>
        <div className={classes.previewContainer}>
          { currentImage ? (
            <CropPreview
              src={currentImage.src}
              originalWidth={currentImage.originalWidth}
              aspectRatio={currentImage.cropWidth / currentImage.cropHeight}
              resizeImageHeight={resizeImageHeight}
              SourceX={currentImage.cropX}
              SourceY={currentImage.cropY}
              SourceWidth={currentImage.cropWidth}
            />) : (
              <div className={classes.emptyPreview} />
            )
          }
        </div>
        {
          !disablePagination && displayAdsInPage.length > 1 && (
          <div className={classes.pagination}>
            <div className={classes.pageControls} style={{ justifyContent: 'space-between' }}>
              <Link
                onClick={goToPrevImage}
                disabled={currentImageIdx === 0}
                className={classes.pageControl}
              >
                <FontIcon className={classes.pageControlPreviousIcon} iconName="ChevronLeft" /> <Text> {i18n.getString(_TL_('Previous ad'))} </Text>
              </Link>
              <div className={classes.paginationIndicator}>
                {displayAdsInPage.map((image, idx) => (
                  <div>
                    <span
                      tabIndex={0}
                      role="button"
                      onKeyDown={() => goToImageIndex(idx)}
                      onClick={() => goToImageIndex(idx)}
                      className={classes.paginationIndicatorDot}
                      style={{ backgroundColor: getPageAdjustedIndex(idx) === getPageAdjustedIndex(currentImageIdx) ? '#0078d4' : '#c8c6c4' }}
                    />
                  </div>
                ))}
              </div>
              <Link
                onClick={goToNextImage}
                disabled={currentImageIdx === displayAdsInPage.length - 1 || displayAdsInPage.length === 0}
                className={classes.pageControl}
              >
                <Text> {i18n.getString(_TL_('Next ad'))} </Text> <FontIcon className={classes.pageControlNextIcon} iconName="ChevronRight" />
              </Link>
            </div>
          </div>
          )
        }
        <div className={classes.previewTag}>
          <Text className={classes.previewTagText}>
            {i18n.getString(_TL_('This is an example of what your display ad might look like. Depending on placement, the actual ad shown may appear differently.'))}
          </Text>
        </div>
      </div>
    </div>
  );
};

AdPreview.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  ads: PropTypes.shape({
    data: PropTypes.instanceOf(Array),
    currentPageIndex: PropTypes.number,
  }).isRequired,
  disablePagination: PropTypes.bool,
  onUpdateCurrentImageIndex: PropTypes.func,
  customStyleFunction: PropTypes.func,
  useCustomStyleFunction: PropTypes.bool,
};

AdPreview.defaultProps = {
  disablePagination: false,
  customStyleFunction: _.noop,
  useCustomStyleFunction: false,
  onUpdateCurrentImageIndex: _.noop,
};
